/* global process, setTimeout */

/**
 * The async trigger, as the add-in waits on it (#636, "OAC Fire-and-Forget Trigger
 * Design", Decisions 3 to 5).
 *
 * With REACT_APP_ASYNC_TRIGGER=true the add-in sends `async=1`: cnp-service stores the
 * mail, queues the run and answers 202 {mailID, runId} at once, and the add-in polls
 * the middle tier's trigger-status until the run ends; where Web PubSub is on, it reads
 * the status when a push says it changed instead (#716). Off, it waits on the one
 * synchronous request as before. Every deployed add-in is built with it on; whether an
 * environment runs async is cnp-service's OAC_ASYNC_TRIGGER (Terraform's async_trigger,
 * #703), which, off, answers 503 async_unavailable and the mail is sent again
 * synchronously. Pure on purpose: no Office, no axios, so the waiting
 * rules are tested on their own (tests/ui/specs/async-run.unit.spec.ts).
 */
export const ASYNC_TRIGGER = process.env.REACT_APP_ASYNC_TRIGGER === "true";

/** The next poll goes 500 ms after the previous answer, so two are never in flight. */
export const POLL_INTERVAL_MS = 500;
/** Longer than any run on dev: the pane says so, and that it can be closed. */
export const SLOW_AFTER_MS = 60 * 1000;
/** Two deliveries of the 4-minute run deadline, plus the queue pickup. */
export const GIVE_UP_AFTER_MS = 9 * 60 * 1000;
/** Failed polls in a row wait longer each time, up to this: a middle tier that is down is not asked twice a second. */
export const MAX_POLL_BACKOFF_MS = 8 * 1000;
/** Office answers 13008 while another token request is pending: a token is asked again 0.5, 1 and 2 s later. */
export const TOKEN_RETRY_DELAYS_MS = [500, 1000, 2000];

/** With pushes (#716): the status is read anyway this often while the socket is open, should a push be lost. */
export const BACKSTOP_MS = 15 * 1000;
/** With pushes: the longest the wait sleeps before it looks again (a cancel, the slow mark), without a request. */
export const TICK_MS = 500;

/** The pause before the next poll, after `failedInARow` failed polls: 500 ms, then 1, 2, 4 and 8 s. */
export const pollDelay = (failedInARow: number) => Math.min(POLL_INTERVAL_MS * 2 ** failedInARow, MAX_POLL_BACKOFF_MS);

/**
 * The run status pushes a wait listens to (#716, helpers/runEvents.ts). A push only
 * says "read now": the outcome always comes from trigger-status, which checks the owner.
 */
export interface RunEvents {
  /** True while the socket is open: then the wait reads on a push, and every BACKSTOP_MS. */
  isOpen(): boolean;
  /** How often the socket has opened: a new open is a read, since a push sent before it is lost. */
  opens(): number;
  /** The last status pushed for `runId` since it was last taken, and forgets it. */
  take(runId: string): RunState | undefined;
  /** Resolves on the next push, open or close, or after `ms`. */
  changed(ms: number): Promise<void>;
}

export type RunState = "queued" | "running" | "done" | "failed" | "superseded";
export type OpenRunState = "queued" | "running";

export interface RunSummary {
  runId: string;
  status: RunState;
  errorKey: string | null;
  clientName: string | null;
  clientPartnerId: string | null;
  /** The client the run was asked for; null for a run older than the field. */
  requestedClientId?: string | null;
}

/** What trigger-status answers (backend/middle-tier/helpers/run-status.js). */
export interface RunStatus extends RunSummary {
  mailID: string;
  /** A finished run that is no longer the mail's current run: the current one, when it is the same user's. */
  current?: RunSummary;
  /** The same, when the current run is another user's: nothing of it is shown. */
  supersededByAnotherUser?: boolean;
}

export type RunOutcome =
  | {
      kind: "done";
      runId: string;
      clientName: string | null;
      clientPartnerId: string | null;
      requestedClientId: string | null;
    }
  | { kind: "failed"; errorKey: string }
  | { kind: "anotherUser" }
  | { kind: "gaveUp"; runId: string; reason?: "auth" }
  | { kind: "cancelled" };

export type Settled = Exclude<RunOutcome, { kind: "gaveUp" } | { kind: "cancelled" }>;
export type DoneRun = Extract<RunOutcome, { kind: "done" }>;

/**
 * The synchronous answer, `clientName: <name>, clientPartnerId: <id>` with `None` for
 * no client, as a done run: what a backend without the async path answers. Anything
 * else is no answer at all (undefined), never "no client".
 */
export function fromSentence(sentence: unknown): DoneRun | undefined {
  if (typeof sentence !== "string" || !/^clientName: .*, clientPartnerId: /.test(sentence)) return undefined;
  const [clientName, clientPartnerId] = sentence.slice(12).split(", clientPartnerId: ");
  const value = (text: string | undefined) => (text === undefined || text === "None" ? null : text);
  return {
    kind: "done",
    runId: "",
    clientName: value(clientName),
    clientPartnerId: value(clientPartnerId),
    requestedClientId: null,
  };
}

const isOpen = (status: RunState): status is OpenRunState => status === "queued" || status === "running";

/**
 * What one status answer means for the pane: the run to keep polling, or how it ended.
 *
 * A finished run that is no longer the mail's current run is not what GOS opens, the
 * current run's extraction is: so the pane follows the current run when it is the
 * same user's, and says another user started one when it is not.
 */
export function settle(answer: RunStatus): Settled | { kind: "poll"; runId: string; status: OpenRunState } {
  if (isOpen(answer.status)) return { kind: "poll", runId: answer.runId, status: answer.status };
  if (answer.supersededByAnotherUser) return { kind: "anotherUser" };
  const run = answer.current ?? answer;
  if (isOpen(run.status)) return { kind: "poll", runId: run.runId, status: run.status };
  if (run.status === "done") {
    const { runId, clientName, clientPartnerId, requestedClientId = null } = run;
    return { kind: "done", runId, clientName, clientPartnerId, requestedClientId };
  }
  // Superseded with its current run gone: nothing is left to open.
  return { kind: "failed", errorKey: run.status === "failed" ? run.errorKey || "error" : "superseded" };
}

export interface WaitOptions {
  runId: string;
  /** The run's status. Rejects with the axios error, so `response.status` says why. */
  fetchStatus: (runId: string) => Promise<RunStatus>;
  onProgress: (progress: { status: OpenRunState; slow: boolean }) => void;
  isCancelled: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /** The run status pushes, where the pane has them (#716): without, or while closed, the wait polls. */
  events?: RunEvents;
}

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * `attempt`, asked again after each of `delays` while it rejects; the last rejection is
 * thrown. A cancelled caller stops the retries, so a token request of a wait that has
 * ended cannot overlap the next run's own (13008).
 */
export async function withRetries<T>(
  attempt: () => Promise<T>,
  delays: number[] = TOKEN_RETRY_DELAYS_MS,
  sleep: (ms: number) => Promise<void> = pause,
  isCancelled: () => boolean = () => false
): Promise<T> {
  for (let tried = 0; ; tried++) {
    try {
      return await attempt();
    } catch (error) {
      if (tried >= delays.length || isCancelled()) throw error;
      await sleep(delays[tried]);
      if (isCancelled()) throw error;
    }
  }
}

/**
 * Polls the run until it ends, the pane gives up (9 min) or the wait is cancelled.
 *
 * A failed poll is asked again, each time a little later (`pollDelay`): a network
 * blip, a timeout, a deploy of the middle tier or a 502 while the table cannot be read
 * says nothing about the run. A 404 does: the run's row is written before the 202, so
 * it is not this user's or it is gone. Any other refusal (4xx) ends the wait too, since
 * asking again would not change it. A 401 or 403, after `fetchStatus` has already
 * tried a fresh token, says nothing about the run either, which goes on: the pane
 * gives up as at 9 minutes, and Check again asks once more (#665).
 *
 * With `events` open (#716) the status is read on a push for the run it follows, on
 * each new open of the socket, and every BACKSTOP_MS, instead of every 500 ms. A push
 * with an open status only moves the progress on; a push never ends the wait itself.
 * Whenever the socket is not open, and after a failed read, it polls as without.
 */
export async function waitForRun({
  runId,
  fetchStatus,
  onProgress,
  isCancelled,
  sleep = pause,
  now = Date.now,
  events,
}: WaitOptions): Promise<RunOutcome> {
  const started = now();
  let polling = runId;
  let last: OpenRunState = "queued";
  let failedInARow = 0;
  let readNow = true;
  let lastRead = started;
  let opens = events ? events.opens() : 0;
  for (;;) {
    if (isCancelled()) return { kind: "cancelled" };
    if (now() - started >= GIVE_UP_AFTER_MS) return { kind: "gaveUp", runId: polling };
    if (readNow) {
      readNow = false;
      lastRead = now();
      // A read covers every push sent before it, a socket's open included: those are
      // dropped, so they start no read of their own. One that comes during the read stays.
      if (events) {
        opens = events.opens();
        events.take(polling);
      }
      let answer: RunStatus | undefined;
      try {
        answer = await fetchStatus(polling);
        failedInARow = 0;
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 404) return { kind: "failed", errorKey: "not_found" };
        if (status === 401 || status === 403) return { kind: "gaveUp", runId: polling, reason: "auth" };
        // 408 and 429 say "later", which is what asking again is.
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          return { kind: "failed", errorKey: "refused" };
        }
        failedInARow += 1;
      }
      if (isCancelled()) return { kind: "cancelled" };
      if (answer) {
        const next = settle(answer);
        if (next.kind !== "poll") return next;
        polling = next.runId;
        last = next.status;
      }
    }
    onProgress({ status: last, slow: now() - started >= SLOW_AFTER_MS });
    const elapsed = now() - started;
    // Nothing waits past the give-up.
    const toGiveUp = GIVE_UP_AFTER_MS - elapsed;
    if (!events || !events.isOpen() || failedInARow > 0) {
      // A backoff never carries the wait past the give-up.
      await sleep(Math.max(0, Math.min(pollDelay(failedInARow), toGiveUp)));
      readNow = true;
      continue;
    }
    // The socket is open. A push that came during the read, for the run now followed (a
    // superseding one), is taken first; otherwise wait for a push, a new open, the
    // backstop, the slow mark or a cancel.
    let pushed = events.take(polling);
    if (!pushed) {
      const toSlow = elapsed < SLOW_AFTER_MS ? SLOW_AFTER_MS - elapsed : Infinity;
      const toBackstop = BACKSTOP_MS - (now() - lastRead);
      await events.changed(Math.max(0, Math.min(TICK_MS, toSlow, toBackstop, toGiveUp)));
      if (!events.isOpen() || events.opens() !== opens) {
        // Closed: read, then poll. Opened again: a push sent meanwhile is lost, so read.
        readNow = true;
        continue;
      }
      pushed = events.take(polling);
    }
    if (pushed && isOpen(pushed)) last = pushed;
    else if (pushed) readNow = true;
    if (now() - lastRead >= BACKSTOP_MS) readNow = true;
  }
}
